import { haversineMeters, bearingDegrees, angleDiffDegrees } from './geo';
import { pkFromMesh, PkMeshVertex } from './pkFromMesh';

/**
 * Detector de cambio de sentido en un tramo de Recorregut de Vigilància —
 * NÚCLEO PURO.
 *
 * Diseño: `docs/superpowers/specs/2026-08-14-rv-cambio-sentido-automatico-design.md`.
 *
 * Tres reglas que hacen esto reproducible y que NO se pueden relajar:
 *   - No usa `Date.now()`: el tiempo entra por el `timestamp` de cada punto.
 *   - No usa `navigator`, React ni Apollo.
 *   - Los umbrales son un parámetro, no constantes incrustadas.
 *
 * Gracias a eso el script de calibración reproduce trazas reales contra ESTE
 * MISMO código y no contra una copia — calibrar una réplica del algoritmo da un
 * número que no describe a la app.
 *
 * La señal maestra es el signo de la variación del PK, porque en el modelo de
 * datos el sentido de un tramo ES eso: `top` = PK creciente, `down` = PK
 * decreciente (ver `normalizeDirectionForQuery` en `utils/surveillanceRoute`).
 */

export type DetectorPoint = {
  lat: number;
  lng: number;
  /** Milisegundos desde epoch. */
  timestamp: number;
  /** Precisión del fix en metros, si el proveedor la da. */
  accuracy?: number;
  /** Velocidad en m/s, si el proveedor la da. */
  speed?: number;
  /** Rumbo del dispositivo en grados, si el proveedor lo da. */
  heading?: number;
};

export type SegmentDirection = 'top' | 'down';

/** El punto de giro real: dónde, con qué PK y cuándo. */
export type Pivot = {
  pkKm: number;
  pkMeter: number;
  lat: number;
  lng: number;
  timestamp: number;
};

export type DetectorDecision =
  | { kind: 'none' }
  | { kind: 'suspect'; pivot: Pivot; direction: SegmentDirection }
  | {
      kind: 'confirm';
      pivot: Pivot;
      direction: SegmentDirection;
      /**
       * Dónde volvió a la vía si hubo un hueco entre el pivote y la
       * confirmación (excursión o parada). El tramo nuevo nace ahí, no en la
       * salida. `null` cuando el giro fue sobre la vía, sin hueco.
       */
      resumedAt?: Pivot | null;
    }
  | { kind: 'blind'; reason: 'no-mesh' | 'off-road' };

export type DetectorThresholds = {
  /** Precisión peor que esto y el punto no cuenta (m). */
  maxAccuracyMeters: number;
  /** Velocidad mínima derivada entre dos puntos para contar como marcha (m/s). */
  minSpeedMs: number;
  /** Retroceso que hace aparecer el aviso (m). */
  suspectMeters: number;
  /** Retroceso que confirma sin más señales (m). */
  confirmMetersBase: number;
  /** Retroceso que confirma con el rumbo real acompañando (m). */
  confirmMetersS2Agree: number;
  /** Descuento cuando el rumbo del dispositivo también acompaña (m). */
  confirmMetersS3Bonus: number;
  /** Suelo absoluto: por debajo no se confirma por mucho que voten (m). */
  confirmMetersFloor: number;
  /** Tolerancia angular contra el eje de la vía (grados). */
  bearingToleranceDegrees: number;
  /** Separación mínima entre dos posiciones para medir un rumbo fiable (m). */
  bearingBaselineMeters: number;
  /** Por debajo de esta velocidad el rumbo del dispositivo es ruido (m/s). */
  minSpeedForHeadingMs: number;
  /** Puntos mínimos de evidencia desde el extremo. */
  minPoints: number;
  /** Segundos mínimos de evidencia desde el extremo. */
  minSeconds: number;
  /** Tras registrar un cambio, segundos en los que no puede haber otro. */
  cooldownSeconds: number;
  /** Tras registrar un cambio, metros en los que no puede haber otro. */
  cooldownMeters: number;
  /** Tras un «No», radio alrededor del extremo rechazado en el que se calla (m). */
  rejectZoneMeters: number;
  /**
   * Hueco entre dos puntos aceptados a partir del cual se da por hecho que la
   * patrulla estuvo fuera de la vía o parada, y se re-siembra la referencia de
   * velocidad SIN perder el extremo (s).
   */
  reseedGapSeconds: number;
  /**
   * Un punto a más de esta distancia de la calzada NO cuenta como evidencia:
   * el detector se declara ciego con él (m).
   */
  maxEvidenceDistanceMeters: number;
  /**
   * Distancia máxima entre etiquetas de PK, EN EL CRUCE, para tratar dos vías
   * como el mismo eje de kilometraje (la C-25z hereda el de la C-25).
   * Heurística LOCAL sobre etiquetas: dentro de ±500 m alrededor de un
   * kilómetro la aritmética de etiquetas vale; no es una posición (ver la
   * regla del kilómetro largo). Dos vías independientes que coincidan en el
   * cruce: del orden del 0,5 % por cruce.
   */
  roadHandoffMaxPkGapMeters: number;
};

export const DEFAULT_THRESHOLDS: DetectorThresholds = {
  maxAccuracyMeters: 30,
  minSpeedMs: 3,
  suspectMeters: 150,
  // 🔴 MEDIDOS, no elegidos a ojo (2026-08-14). Calibración contra 18
  // recorridos de beta con traza GPS REAL —descartando los sembrados por
  // script, que se delatan por tener 3 valores distintos de Δt y `accuracy`
  // uniforme aleatoria—, verificando cada detección contra la inversión
  // física del PK en vez de contra la etiqueta `direction` del tramo (de 36
  // giros declarados, solo 13 tenían inversión real: el resto eran pruebas de
  // interfaz). Resultado: a 400 m salían 12 aciertos y 2 falsos positivos; a
  // partir de 500 los 2 falsos desaparecen SIN perder ninguna detección
  // buena, y se mantiene igual hasta 800 → hay margen, no es un filo.
  confirmMetersBase: 500,
  confirmMetersS2Agree: 300,
  confirmMetersS3Bonus: 50,
  confirmMetersFloor: 250,
  bearingToleranceDegrees: 30,
  bearingBaselineMeters: 50,
  minSpeedForHeadingMs: 5.5, // ~20 km/h
  minPoints: 3,
  minSeconds: 20,
  cooldownSeconds: 60,
  cooldownMeters: 200,
  rejectZoneMeters: 1000,
  // La cadencia normal es un punto cada ~5 s; la tablet deja de emitir puntos
  // parada (compuerta de 5 m) o fuera de la vía (el núcleo los descarta). Un
  // minuto sin puntos aceptados no es ruido: es que pasó algo.
  reseedGapSeconds: 60,
  // 🔴 MEDIDO (2026-09-02). `pkFromMesh` acepta hasta 300 m para que el PK
  // sin cobertura salga en una salida larga o un surtidor, pero eso NO es
  // «estar en la vía»: al salir del ramal por la N-156 los puntos iban a
  // 56 → 253 m con el PK bajando y valían como 193 m de retroceso — aviso
  // falso tres veces en un día (13:16, 18:56, 21:16), y una vía paralela del
  // km 217 pasó por dos giros. La vuelta de verdad va a 1-7 m. Con 50 m los
  // 14 giros reales del día siguen saliendo y los falsos desaparecen; es el
  // mismo número que el margen de ambigüedad, y por el mismo motivo: por
  // debajo de 50 m dos calzadas comparten asfalto. Con 30 el pivote se
  // adelanta hasta 110 m en los enlaces (la rampa deja de contar).
  maxEvidenceDistanceMeters: 50,
  roadHandoffMaxPkGapMeters: 500,
};

/**
 * Voto de las señales de rumbo. Devuelve cuántos metros de retroceso hay que
 * exigir para confirmar, o `null` si hay **VETO** (el rumbo real contradice al
 * PK, así que la malla mintió).
 *
 * S2 —el rumbo entre dos posiciones separadas lo bastante, contra el eje de la
 * vía— es la única señal que puede vetar: es la que caza que el PK esté mal.
 * S3 —el rumbo que reporta el dispositivo— solo descuenta metros y **nunca
 * decide**: en una antena única el rumbo se deriva del propio movimiento, es
 * ruido a baja velocidad y falta en muchos equipos.
 */
export function requiredMetersFor(
  measured: SegmentDirection,
  axisBearing: number | null,
  travelBearing: number | null,
  deviceHeading: number | null,
  thresholds: DetectorThresholds
): number | null {
  if (axisBearing == null || travelBearing == null) {
    return thresholds.confirmMetersBase;
  }
  // El eje apunta hacia PK creciente: circular en `down` es ir al revés.
  const expected = measured === 'top' ? axisBearing : (axisBearing + 180) % 360;
  const travelDiff = angleDiffDegrees(expected, travelBearing);
  if (travelDiff > 180 - thresholds.bearingToleranceDegrees) return null;
  if (travelDiff > thresholds.bearingToleranceDegrees) {
    return thresholds.confirmMetersBase;
  }
  let required = thresholds.confirmMetersS2Agree;
  if (
    deviceHeading != null &&
    angleDiffDegrees(expected, deviceHeading) <=
      thresholds.bearingToleranceDegrees
  ) {
    required -= thresholds.confirmMetersS3Bonus;
  }
  return Math.max(thresholds.confirmMetersFloor, required);
}

export type DetectorContext = {
  /** La calzada sobre la que se proyecta el PK (la del tramo). */
  mesh: PkMeshVertex[];
  /**
   * TODAS las calzadas de esa vía, para decidir si el punto pisa asfalto. Al
   * dar la vuelta en una autovía la patrulla pasa a la otra calzada, que en
   * la C-17 llega a 84 m (p95) de la vigilada: medir el tope de evidencia
   * solo contra `mesh` la dejaría ciega justo cuando más importa. Si falta,
   * se usa `mesh`.
   */
  carriageways?: PkMeshVertex[][];
  thresholds: DetectorThresholds;
};

export type Sample = { meters: number; point: DetectorPoint };

export type DetectorState = {
  /** Sentido observado del movimiento. `null` hasta que hay dos muestras. */
  observed: SegmentDirection | null;
  /** PK del extremo alcanzado en el sentido observado, en metros. */
  extremeMeters: number | null;
  /** El extremo como pivote listo para usar. */
  extremePivot: Pivot | null;
  /** Última muestra aceptada. */
  previous: Sample | null;
  /** Muestras acumuladas desde el extremo (para `minPoints`). */
  sinceExtremePoints: number;
  /** Ya se emitió `suspect` para este extremo. */
  suspectEmitted: boolean;
  /**
   * Ancla desde la que se mide el rumbo real (S2). Se re-ancla cada vez que la
   * patrulla se aleja de ella más de `bearingBaselineMeters`, de forma que el
   * rumbo se calcula siempre sobre una ventana de ese tamaño: con los ~16 m que
   * separan dos lecturas a 60 km/h, medir entre puntos consecutivos daría un
   * rumbo dominado por el ruido del GPS.
   */
  baseline: Sample | null;
  /** Último rumbo real medido, en grados. `null` hasta que hay ventana. */
  lastTravelBearing: number | null;
  /** Dónde y cuándo se confirmó el último cambio (para el cooldown). */
  lastConfirmAt: Sample | null;
  /** PK del extremo que el operario rechazó con un «No». */
  rejectedExtreme: Sample | null;
  /**
   * Dónde volvió a la vía tras un hueco. Viaja con el pivote en la
   * confirmación para que el tramo nuevo nazca en la reincorporación y no en
   * la salida. Se olvida en cuanto el extremo avanza: el hueco ya no está
   * entre el pivote y ahora.
   */
  resumedAt: Pivot | null;
};

export function createDetectorState(): DetectorState {
  return {
    observed: null,
    extremeMeters: null,
    extremePivot: null,
    previous: null,
    sinceExtremePoints: 0,
    suspectEmitted: false,
    baseline: null,
    lastTravelBearing: null,
    lastConfirmAt: null,
    rejectedExtreme: null,
    resumedAt: null,
  };
}

/**
 * El operario dijo «No». Se marca el extremo pendiente como rechazado: el
 * detector se calla hasta salir de su zona.
 *
 * Sin esto el aviso reaparecería en el punto siguiente y machacaría al
 * operario conduciendo — el mismo error que hubo que corregir en el
 * disparador del modal de cierre de OT.
 */
export function rejectPending(state: DetectorState): DetectorState {
  return {
    ...state,
    // La muestra completa (con punto), no solo los metros: un cambio de vía la
    // re-proyecta sobre la malla nueva (`carryStateAcrossRoads`).
    rejectedExtreme:
      state.extremePivot && state.extremeMeters != null
        ? {
            meters: state.extremeMeters,
            point: {
              lat: state.extremePivot.lat,
              lng: state.extremePivot.lng,
              timestamp: state.extremePivot.timestamp,
            },
          }
        : null,
    suspectEmitted: false,
  };
}

export function stepDetector(
  state: DetectorState,
  point: DetectorPoint,
  ctx: DetectorContext
): { state: DetectorState; decision: DetectorDecision } {
  const { mesh, thresholds } = ctx;

  if (mesh.length === 0) {
    return { state, decision: { kind: 'blind', reason: 'no-mesh' } };
  }

  if (
    point.accuracy != null &&
    point.accuracy > thresholds.maxAccuracyMeters
  ) {
    return { state, decision: { kind: 'none' } };
  }

  const pk = pkFromMesh(point.lat, point.lng, mesh);
  // Ciego si no proyecta, o si no pisa asfalto de la vía: una vía paralela,
  // una rampa o la carretera por la que se abandona el ramal proyectan sobre
  // la malla con el PK moviéndose, y eso no es evidencia de nada (ver
  // `maxEvidenceDistanceMeters`). «Pisar asfalto» se mide contra CUALQUIER
  // calzada de la vía: tras la vuelta se circula por la otra.
  const pisaAsfalto =
    pk != null &&
    (ctx.carriageways ?? [mesh]).some(
      (calzada) =>
        pkFromMesh(
          point.lat,
          point.lng,
          calzada,
          thresholds.maxEvidenceDistanceMeters
        ) != null
    );
  if (!pk || !pisaAsfalto) {
    return { state, decision: { kind: 'blind', reason: 'off-road' } };
  }

  // 🔴 La posición se mide en METROS DE ASFALTO (`alongMeters`), no en el total
  // del PK.
  //
  // El detector acumula evidencia en metros hasta confirmar a los 500. Con el
  // total del PK, cruzar un kilómetro largo la falsea: entre `84+1800` y
  // `85+000` el total BAJA 800 mientras la patrulla avanza 90, y esos 800 m de
  // «retroceso» bastaban por sí solos para confirmar una vuelta que nadie dio.
  // Y no es raro: la C-25 tiene 72 kilómetros largos de sus 153.
  //
  // `alongMeters` es monótono por construcción —son metros recorridos sobre la
  // malla—, así que el signo y la magnitud vuelven a significar lo que el
  // detector cree que significan.
  const meters = pk.alongMeters;
  const sample: Sample = { meters, point };
  const pivot: Pivot = {
    pkKm: pk.pkKm,
    pkMeter: pk.pkMeter,
    lat: point.lat,
    lng: point.lng,
    timestamp: point.timestamp,
  };

  // Primera muestra: solo siembra el estado, no hay nada que comparar.
  if (!state.previous) {
    return {
      state: {
        ...state,
        previous: sample,
        // Una memoria traspasada de otra vía (`carryStateAcrossRoads`) no se
        // pisa: el extremo sigue siendo el de antes del cruce.
        extremeMeters: state.extremeMeters ?? meters,
        extremePivot: state.extremePivot ?? pivot,
        baseline: sample,
      },
      decision: { kind: 'none' },
    };
  }

  // Compuerta de velocidad: la DERIVADA de las posiciones, no la que reporta
  // el proveedor — que a veces falta y a veces marca movimiento estando
  // parado. Esto es lo que descarta las maniobras en el lugar de una
  // actuación, que son el falso positivo más caro.
  const dtSeconds = (point.timestamp - state.previous.point.timestamp) / 1000;

  // 🔴 Puerta de hueco (2026-09-02). Un hueco largo entre dos puntos aceptados
  // es una excursión fuera de la vía (el núcleo descarta esos puntos como
  // ciegos) o una parada sin puntos (la compuerta de 5 m de la tablet). La
  // compuerta de velocidad de abajo, medida contra el punto ANTERIOR al hueco,
  // bloqueaba 3 m/s × segundos de hueco: 20 min fuera = 3,6 km ciego al volver,
  // y el giro se perdía. Medido en la C-25 el 2026-09-02: cuatro giros dados
  // saliendo por un enlace, por la A-2 o por un lazo, ninguno registrado.
  //
  // Se re-siembran aquí la referencia de velocidad y el ancla del rumbo,
  // CONSERVANDO el extremo y su pivote: la salida de la vía sigue siendo el
  // sitio del giro, y si vuelve en el mismo sentido el extremo avanza y no
  // pasa nada. El punto de vuelta se recuerda para que el tramo nuevo nazca en
  // la reincorporación, no en la salida.
  //
  // Y el «No» que el operario dio ANTES del hueco no vale para la vuelta: lo
  // dijo de otra aproximación (2026-09-02 21:16: salió del ramal, se le
  // avisó, dijo «No»; volvió 12 min después de verdad y la zona de rechazo
  // se tragó el aviso — el corte llegó un kilómetro más tarde y sin
  // preguntar). Una aproximación nueva es una pregunta nueva.
  if (dtSeconds > thresholds.reseedGapSeconds) {
    return {
      state: {
        ...state,
        previous: sample,
        baseline: sample,
        resumedAt: pivot,
        rejectedExtreme: null,
        suspectEmitted: false,
      },
      decision: { kind: 'none' },
    };
  }

  const distance = haversineMeters(
    state.previous.point.lat,
    state.previous.point.lng,
    point.lat,
    point.lng
  );
  const derivedSpeed = dtSeconds > 0 ? distance / dtSeconds : 0;
  if (derivedSpeed < thresholds.minSpeedMs) {
    return { state, decision: { kind: 'none' } };
  }

  // Ventana de rumbo (S2): se re-ancla al superar la separación mínima, así
  // que tras un giro la primera ventana puede quedar a caballo del pivote —
  // pero para entonces el retroceso acumulado aún es pequeño y no puede
  // confirmar nada. Las siguientes ya son limpias.
  const anchor = state.baseline ?? state.previous;
  const anchorDistance = haversineMeters(
    anchor.point.lat,
    anchor.point.lng,
    point.lat,
    point.lng
  );
  const reanchor = anchorDistance >= thresholds.bearingBaselineMeters;
  const travelBearing = reanchor
    ? bearingDegrees(anchor.point.lat, anchor.point.lng, point.lat, point.lng)
    : state.lastTravelBearing;
  const baseline = reanchor ? sample : anchor;

  // El rumbo del dispositivo solo se mira por encima de la velocidad a la que
  // deja de ser ruido (antena única: el rumbo se deriva del movimiento).
  const deviceHeading =
    derivedSpeed >= thresholds.minSpeedForHeadingMs
      ? (point.heading ?? null)
      : null;

  const delta = meters - state.previous.meters;
  const movingDirection: SegmentDirection = delta >= 0 ? 'top' : 'down';
  const observed = state.observed ?? movingDirection;
  const extremeMeters = state.extremeMeters ?? meters;

  // ¿Sigue avanzando en el sentido observado? Entonces el extremo avanza con
  // él y la evidencia acumulada se descarta.
  const advancing =
    observed === 'top' ? meters >= extremeMeters : meters <= extremeMeters;
  if (advancing) {
    return {
      state: {
        ...state,
        observed,
        previous: sample,
        extremeMeters: meters,
        extremePivot: pivot,
        sinceExtremePoints: 0,
        suspectEmitted: false,
        baseline,
        lastTravelBearing: travelBearing,
        // El hueco, si lo hubo, queda atrás del extremo: ya no describe nada.
        resumedAt: null,
      },
      decision: { kind: 'none' },
    };
  }

  // Retrocede: se acumula evidencia contra el extremo.
  const back = Math.abs(meters - extremeMeters);
  const sinceExtremePoints = state.sinceExtremePoints + 1;
  const elapsedSeconds = state.extremePivot
    ? (point.timestamp - state.extremePivot.timestamp) / 1000
    : 0;
  const enoughEvidence =
    sinceExtremePoints >= thresholds.minPoints &&
    elapsedSeconds >= thresholds.minSeconds;
  const reversed: SegmentDirection = observed === 'top' ? 'down' : 'top';
  const next: DetectorState = {
    ...state,
    observed,
    previous: sample,
    sinceExtremePoints,
    baseline,
    lastTravelBearing: travelBearing,
  };

  const required = requiredMetersFor(
    reversed,
    pk.axisBearing,
    travelBearing,
    deviceHeading,
    thresholds
  );

  // Acabamos de registrar un cambio: ni tiempo ni distancia suficientes para
  // que haya otro. Sin esto, el retroceso que ya se había acumulado seguiría
  // contando y encadenaría un segundo tramo de la nada.
  const inCooldown =
    state.lastConfirmAt != null &&
    ((point.timestamp - state.lastConfirmAt.point.timestamp) / 1000 <
      thresholds.cooldownSeconds ||
      Math.abs(meters - state.lastConfirmAt.meters) < thresholds.cooldownMeters);

  const inRejectZone =
    state.rejectedExtreme != null &&
    Math.abs(meters - state.rejectedExtreme.meters) <
      thresholds.rejectZoneMeters;

  if (inCooldown || inRejectZone) {
    return { state: next, decision: { kind: 'none' } };
  }

  if (
    state.extremePivot &&
    enoughEvidence &&
    required != null &&
    back >= required
  ) {
    return {
      state: {
        ...createDetectorState(),
        observed: reversed,
        previous: sample,
        extremeMeters: meters,
        extremePivot: pivot,
        baseline,
        lastTravelBearing: travelBearing,
        lastConfirmAt: sample,
      },
      decision: {
        kind: 'confirm',
        pivot: state.extremePivot,
        direction: reversed,
        resumedAt: state.resumedAt,
      },
    };
  }

  if (
    state.extremePivot &&
    !state.suspectEmitted &&
    back >= thresholds.suspectMeters
  ) {
    return {
      state: { ...next, suspectEmitted: true },
      decision: { kind: 'suspect', pivot: state.extremePivot, direction: reversed },
    };
  }

  return { state: next, decision: { kind: 'none' } };
}

/**
 * ¿Dos etiquetas de PK son el mismo eje en el cruce? Resta LOCAL, acotada por
 * `maxGapMeters` (≤ 500 m): no es una posición a lo largo de la carretera.
 */
export function isPkContinuous(
  a: { pkKm: number; pkMeter: number },
  b: { pkKm: number; pkMeter: number },
  maxGapMeters: number
): boolean {
  return (
    Math.abs(a.pkKm * 1000 + a.pkMeter - (b.pkKm * 1000 + b.pkMeter)) <=
    maxGapMeters
  );
}

export type RoadHandoff = {
  /** Las dos vías son el mismo eje de PK en el cruce (ver `isPkContinuous`). */
  continuous: boolean;
  /** Calzada de la vía nueva sobre la que se proyecta a partir de ahora. */
  mesh: PkMeshVertex[];
  /**
   * Solo si `!continuous`: referencia con la que arranca el detector (el
   * sentido declarado del tramo, en la vía del propio tramo), o null.
   */
  fallbackObserved?: SegmentDirection | null;
};

/**
 * Qué sobrevive del detector al cambiar de vía.
 *
 * Sin continuidad: nace de nuevo (como siempre), con la referencia sembrada si
 * hay red. Con continuidad: la referencia se conserva y cada muestra con
 * posición se RE-PROYECTA sobre la malla nueva; lo que cae a más del tope de
 * evidencia se descarta. Si el pivote no proyecta, la geometría nace de cero
 * pero la referencia se conserva.
 *
 * Motivo: C-25 → C-25z, 2026-09-09. `createDetectorState()` en el cruce borró
 * el pivote 5 s antes de una vuelta dentro del ramal, y el primer movimiento
 * aceptado después (que ya iba al revés) se convirtió en «lo normal»: 2,7 km
 * sin registrar, cortados a mano. La premisa «los metros de dos carreteras no
 * son comparables» es cierta en general y falsa justo ahí: la C-25z hereda el
 * kilometraje de la C-25.
 */
export function carryStateAcrossRoads(
  state: DetectorState,
  handoff: RoadHandoff,
  thresholds: DetectorThresholds
): DetectorState {
  if (!handoff.continuous) {
    return { ...createDetectorState(), observed: handoff.fallbackObserved ?? null };
  }
  const project = (p: { lat: number; lng: number }) =>
    pkFromMesh(p.lat, p.lng, handoff.mesh, thresholds.maxEvidenceDistanceMeters);
  const sample = (s: Sample | null): Sample | null => {
    if (!s) return null;
    const hit = project(s.point);
    return hit ? { meters: hit.alongMeters, point: s.point } : null;
  };
  const pivot = (p: Pivot | null): { pivot: Pivot; meters: number } | null => {
    if (!p) return null;
    const hit = project(p);
    return hit
      ? { pivot: { ...p, pkKm: hit.pkKm, pkMeter: hit.pkMeter }, meters: hit.alongMeters }
      : null;
  };
  const extreme = pivot(state.extremePivot);
  return {
    observed: state.observed,
    extremeMeters: extreme?.meters ?? null,
    extremePivot: extreme?.pivot ?? null,
    previous: sample(state.previous),
    sinceExtremePoints: extreme ? state.sinceExtremePoints : 0,
    suspectEmitted: extreme ? state.suspectEmitted : false,
    baseline: sample(state.baseline),
    lastTravelBearing: state.lastTravelBearing,
    lastConfirmAt: sample(state.lastConfirmAt),
    rejectedExtreme: sample(state.rejectedExtreme),
    resumedAt: pivot(state.resumedAt)?.pivot ?? null,
  };
}
